import { useCallback, useState } from 'react';

/**
 * Hook for managing draft/uncommitted state derived from a base prop or external value.
 *
 * Automatically discards the draft when the base value reference changes (e.g., after
 * refetch or switching entities), following React's recommended pattern for adjusting
 * state when a prop changes without using useEffect.
 */
export const useDraftState = <T>(base: T) => {
  const [draft, setDraft] = useState<{ base: T; value: T } | null>(null);

  if (draft !== null && draft.base !== base) {
    setDraft(null);
  }

  const value = draft !== null && draft.base === base ? draft.value : base;

  const updateDraft = useCallback(
    (changes: Partial<T> | ((prev: T) => T)) => {
      setDraft((prev) => {
        const current = prev !== null && prev.base === base ? prev.value : base;
        const nextValue =
          typeof changes === 'function'
            ? (changes as (prev: T) => T)(current)
            : typeof current === 'object' &&
                current !== null &&
                !Array.isArray(current)
              ? { ...current, ...changes }
              : (changes as unknown as T);
        return { base, value: nextValue };
      });
    },
    [base]
  );

  const resetDraft = useCallback(() => {
    setDraft(null);
  }, []);

  return [value, updateDraft, resetDraft] as const;
};

export default useDraftState;
